import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { List } from "immutable";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import { addNote, editNote } from "~/store/actions/actions.notes";
import { updateSubscribers } from "~/store/actions/actions.subscription";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { getInstanceId } from "~/store/selectors/selector.instance";
import { getNoteById } from "~/store/selectors/selector.notes";
import {
  getProjectSubscriberIds,
  getSortedSubscribersByProjectId,
  getSubscriberIdsByTypeAndId,
} from "~/store/selectors/selector.subscription";
import { getLoggedUserId, isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import Form from "./Form";
import { useDraft } from "../Hooks/useDraft";
import { showSuccessToast, showErrorToast } from "../Toast";

const NoteFormContainer = ({
  noteId,
  notebookId,
  projectId,
  onClose,
  submitButtonLabel,
  shouldOverflow,
  className,
}) => {
  const [draft, setDraft] = useDraft("note");
  const loggedUserId = useSelector(getLoggedUserId);
  const instanceId = useSelector(getInstanceId);
  const draftId = noteId
    ? `instance_${instanceId}_user_${loggedUserId}_${projectId}_${notebookId}_${noteId}`
    : `instance_${instanceId}_user_${loggedUserId}_${projectId}_${notebookId}`;
  const hasDraft = !!(draft && draft[draftId]);

  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const defaultHideFromClients = useSelector((state) => getRawConfigOption(state, "default_hide_from_clients"));
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));

  const defaultHiddenFromClients = isLoggedUserClient ? false : defaultHideFromClients;

  // on existing note
  const noteSubscriberIds = useSelector((state) => getSubscriberIdsByTypeAndId(state, "Note", noteId), isEqual);

  // before note exist
  const initialSubscriberIds = useSelector((state) => getProjectSubscriberIds(state, projectId));

  const note = useSelector((state) => getNoteById(state, noteId));
  const projectMembers = useSelector((state) => getSortedSubscribersByProjectId(state, projectId));

  const handleSaveDraft = useCallback(
    (attributes) => {
      setDraft((prevState) => {
        return {
          ...prevState,
          [draftId]: attributes,
        };
      });
    },
    [draftId, setDraft]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRemoveDraft = useCallback(() => {
    const { [draftId]: current, ...rest } = draft;
    setDraft(rest);
  }, [draftId, setDraft, draft]);

  const handleClose = useCallback(() => {
    const promise = new Promise((resolve) => {
      setDraft((prevState) => {
        if (!prevState) return undefined;
        const { [draftId]: current, ...rest } = prevState;
        return rest;
      });
      setTimeout(() => {
        resolve(true);
      }, 100);
    });
    promise.then(() => {
      if (typeof onClose === "function") {
        onClose();
      }
    });
  }, [draftId, onClose, setDraft]);

  const handleSubmit = useCallback(
    async (attributes) => {
      handleRemoveDraft();
      if (noteId) {
        const first = [...attributes.subscribers].sort();
        const second = [...noteSubscriberIds].sort();
        if (!isEqual(first, second)) {
          await dispatch(updateSubscribers(noteId, "note", attributes.subscribers));
        }
        await dispatch(editNote(projectId, noteId, attributes));
      } else {
        const action = await dispatch(addNote(projectId, { ...attributes, notebook_id: notebookId }));
        if (action.type === "ADD_NOTE_SUCCESS") {
          navigate(`?modal=Note-${action.response.result.single}-${projectId}`);
          showSuccessToast(t("Note created"));
        } else {
          showErrorToast(t("Failed to create note"));
        }
      }
      handleClose();
    },
    [handleRemoveDraft, noteId, handleClose, noteSubscriberIds, dispatch, projectId, notebookId, navigate, t]
  );

  const formData = useMemo(() => {
    let object = {};
    if (hasDraft) {
      object = {
        ...draft[draftId],
        project_id: projectId,
        notebook_id: notebookId,
      };
    } else {
      if (noteId && note) {
        object = {
          ...note,
          project_id: projectId,
          notebook_id: notebookId,
          subscribers: noteSubscriberIds,
        };
      }
    }
    return object;
  }, [draft, draftId, hasDraft, note, noteId, noteSubscriberIds, notebookId, projectId]);

  const {
    id,
    name,
    body,
    notebook_id,
    is_pinned,
    is_hidden_from_clients = defaultHiddenFromClients,
    subscribers = initialSubscriberIds,
    drop_attached_files,
    attach_uploaded_files,
  } = formData;

  return (
    <Form
      notebookId={notebook_id}
      projectId={projectId}
      onChange={handleSaveDraft}
      onSubmit={handleSubmit}
      hasDraft={hasDraft}
      id={id}
      name={name}
      body={body}
      isPinned={is_pinned}
      isHiddenFromClients={is_hidden_from_clients}
      subscribers={subscribers}
      onCancel={handleClose}
      submitButtonLabel={submitButtonLabel}
      isLoggedUserClient={isLoggedUserClient}
      attachments={new List(note?.attachments)}
      dropAttachedFiles={drop_attached_files}
      attachUploadedFiles={attach_uploaded_files}
      hideFromClients={hideFromClients}
      className={className}
      projectMembers={projectMembers}
      shouldOverflow={shouldOverflow}
    />
  );
};

NoteFormContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
  noteId: PropTypes.number,
  notebookId: PropTypes.number,
  submitButtonLabel: PropTypes.string,
  onClose: PropTypes.func,
  shouldOverflow: PropTypes.bool,
  className: PropTypes.string,
};

NoteFormContainer.defaultProps = {
  notebookId: 0,
};

export default NoteFormContainer;
